/** @jsx jsx */

import React, { useRef, ReactElement } from 'react';
import { css, jsx } from '@emotion/core';
import { useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { reorderChildren } from 'factories/block';
import { setBlockAction } from 'actions/setBlockAction';
import { Block } from 'factories/block';
import plus from 'assets/Plus_purple.svg';
import bin from 'assets/Delete_new.svg';

export const Widget = ({
  label,
  removeItem,
  children,
  background = '#e8edff' || '#c0d4fe' || '#282a2b',
}: {
  label: string;
  removeItem: () => void;
  children?: any;
  background?: string;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        height: 30,
        background,
        borderRadius: 3,
        margin: '5px 0',
        padding: '5px 10px',
        color: '#282a2b' || '#fff',
        fontFamily: 'inherit',
        fontSize: 12,
      }}
    >
      {label}

      <div
        css={css({
          display: 'flex',
          '> *': {
            margin: '0 0 0 5px',
          },
        })}
      >
        {children}

        <div
          css={css({
            width: 20,
            height: 20,
            background: `#fff url('${bin}') no-repeat`,
            backgroundPosition: 'center',
            backgroundSize: '50%',
            borderRadius: '50%',
            cursor: 'pointer',
            ':hover': {
              opacity: 0.8,
            },
          })}
          onClick={removeItem}
        />
      </div>
    </div>
  );
};

interface IReorderInteractionWidget {
  block: Block | undefined;
  isDragDisabled?: boolean | undefined;
  onDragEnd?: (data: unknown) => void;
  addItem: () => void;
  removeItem: (index: number) => void;
  getLabel: (index: number) => string | undefined;
  children?: any;
}

const ReorderInteractionWidget = ({
  block,
  isDragDisabled,
  onDragEnd,
  addItem,
  removeItem,
  getLabel,
  children,
}: IReorderInteractionWidget) => {
  //
  const dispatch = useDispatch();

  if (!block) {
    console.warn('there is no block here !!!');
    return null;
  }

  const blockChildren = block?.children as Block[];

  const prevIndex = useRef<number | undefined>(undefined);

  return (
    <div>
      <DragDropContext
        onDragEnd={
          onDragEnd
            ? onDragEnd
            : data => {
                if (!data.destination) {
                  return;
                }

                const { draggableId, source, destination } = data;
                const startIndex = source.index;
                const endIndex = destination.index;
                const newOrder = reorderChildren(block, startIndex, endIndex);

                dispatch(setBlockAction(newOrder));
              }
        }
      >
        <Droppable droppableId="widget-container" type="widget-container">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {blockChildren.map((entry: any, index) => {
                const { id } = entry;
                const draggableId = `draggable_${id}`;
                const label = getLabel(index);

                return (
                  <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    isDragDisabled={isDragDisabled}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        css={css({
                          top: 'auto !important',
                          left: 'auto !important',
                        })}
                      >
                        <Widget
                          label={label || id}
                          removeItem={() => {
                            removeItem(index);
                          }}
                          children={
                            children
                              ? React.cloneElement(
                                  children,

                                  { index }
                                )
                              : null
                          }
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div>
        <div
          css={css({
            width: 20,
            height: 20,
            background: `url('${plus}') no-repeat`,
            backgroundPosition: 'center',
            backgroundSize: '50%',
            borderRadius: '50%',
            border: '1px solid #c0d4fe',
            cursor: 'pointer',
            ':hover': {
              opacity: 0.8,
            },
          })}
          onClick={addItem}
        ></div>
      </div>
    </div>
  );
};

export { ReorderInteractionWidget };
